/**
 * Carril [A] · PR2 — Centro de ayuda / Preguntas frecuentes (por vertical).
 * Contextual: alcanzable desde el sidebar (item "Ayuda") y el topbar ("¿Cómo
 * funciona?" re-lanza el tour). El contenido sale del mapa editable helpContent.ts
 * y se ramifica por los módulos del tenant (un GastroGest no ve ayuda de gimnasio).
 * Incluye "Llévame ahí" (salta a la pantalla) y re-lanzar el tour / reiniciar
 * los consejos progresivos.
 */
import { useState } from 'react';
import { LifeBuoy, ChevronDown, ArrowRight, PlayCircle, RotateCcw } from 'lucide-react';
import { helpGroupsFor, type HelpMe } from './helpContent';
import { resetCoachSeen } from './coach';

export function HelpCenter({ me, userId, onNavigate, onStartTour }: {
  me: HelpMe;
  userId?: string;
  onNavigate: (key: string) => void;
  onStartTour: () => void;
}) {
  const groups = helpGroupsFor(me);
  const [open, setOpen] = useState<string>(groups[0]?.vertical ?? '');
  const [resetMsg, setResetMsg] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-2xl tracking-tight text-slate-900">
            <LifeBuoy className="h-6 w-6 text-slate-400" /> Centro de ayuda
          </h2>
          <p className="text-sm text-slate-500">Cómo usar cada sección de tu espacio. Solo verás lo que aplica a tu negocio.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onStartTour}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-[var(--brand-primary-fg)] shadow-sm transition hover:bg-[var(--brand-primary-hover)]">
            <PlayCircle className="h-4 w-4" /> Ver el tour
          </button>
          <button onClick={() => { resetCoachSeen(userId); setResetMsg(true); }}
            title="Vuelve a mostrar los consejos al entrar a cada sección"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
            <RotateCcw className="h-4 w-4" /> Reiniciar consejos
          </button>
        </div>
      </div>
      {resetMsg && (
        <p className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          Listo: los consejos volverán a aparecer la próxima vez que entres a cada sección.
        </p>
      )}

      {groups.map((g) => {
        const expanded = open === g.vertical;
        return (
          <section key={g.vertical} className="card-soft overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
            <button type="button" onClick={() => setOpen(expanded ? '' : g.vertical)}
              aria-expanded={expanded}
              className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left transition hover:bg-slate-50/70 md:px-8">
              <span className="flex items-center gap-3">
                <span className="font-serif text-lg tracking-tight text-slate-900">{g.label}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">{g.entries.length}</span>
              </span>
              <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {g.entries.map((e) => (
                  <div key={e.key} className="px-6 py-4 md:px-8">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-medium text-slate-900">{e.title}</h3>
                      <button onClick={() => onNavigate(e.key)}
                        className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-[var(--brand-primary)] hover:underline">
                        Llévame ahí <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{e.coach}</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                      {e.faq.map((line, i) => <li key={i}>{line}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {groups.length === 0 && (
        <p className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center text-sm text-slate-400">
          Aún no hay ayuda para tu configuración actual.
        </p>
      )}
    </div>
  );
}
